import { useState, useMemo } from "react"
import {
  Search,
  Plus,
  Pencil,
  Archive,
  Trash2,
  Copy,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Eye,
  EyeOff,
} from "lucide-react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table"
import { Badge } from "../components/ui/badge"
import { Switch } from "../components/ui/switch"
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "../components/ui/dialog"
import { useLogs } from "../components/LogProvider"
import { useLocale } from "../components/LocaleProvider"
import { useAuth } from "../components/AuthProvider"
import { toast } from "../components/ui/toast"
import { type CreateAccountDto, type ClientOption, type ProxyOption } from "../lib/api"
import {
  useAccounts,
  useClients as useClientOptions,
  useCreateAccount,
  useUpdateAccount,
  useRevealAccountPassword,
  useArchiveAccount,
  useDeleteAccount,
  useProxies,
} from "../lib/queries"
import { getUserColor } from "../lib/utils"
import { countryFlag, formatProxyLocation } from "../lib/country"
import type { Account, AccountProxy, AccountStatus } from "../types"
import type { TranslationKey } from "../i18n/translations"

const STATUS_OPTIONS: AccountStatus[] = ["ACTIVE", "NOT USED", "BLOCKED", "ARCHIVE"]

const ACCOUNT_STATUS_T_KEY: Record<string, TranslationKey> = {
  ACTIVE: "status_active",
  "NOT USED": "status_not_used",
  BLOCKED: "status_blocked",
  ARCHIVE: "status_archive",
}

const STRATEGY_OPTIONS = [
  "Active",
  "Passive 1",
  "Passive 2",
  "Passive 3",
  "Passive 4",
  "Passive 5",
]

const STRATEGY_T_KEY: Record<string, TranslationKey> = {
  Active: "strategy_active",
  Outsource: "strategy_outsource",
  "Passive 1": "strategy_passive_1",
  "Passive 2": "strategy_passive_2",
  "Passive 3": "strategy_passive_3",
  "Passive 4": "strategy_passive_4",
  "Passive 5": "strategy_passive_5",
}

const STATUS_VARIANT: Record<AccountStatus, "success" | "warning" | "danger" | "info" | "neutral"> =
  {
    ACTIVE: "success",
    "NOT USED": "neutral",
    BLOCKED: "danger",
    ARCHIVE: "info",
  }

const STRATEGY_VARIANT: Record<string, "success" | "info" | "warning" | "neutral"> = {
  Active: "success",
  Outsource: "info",
  "Passive 1": "neutral",
  "Passive 2": "neutral",
  "Passive 3": "neutral",
  "Passive 4": "neutral",
  "Passive 5": "neutral",
}

const emptyForm: Account = {
  accountId: "",
  client: "",
  email: "",
  status: "ACTIVE",
  strategy: "",
  creationDate: "",
  comment: "",
  eldersOnly: false,
  hasPassword: false,
  password: "",
  proxy: null,
}

function statusVariant(status: AccountStatus) {
  return STATUS_VARIANT[status] ?? "neutral"
}

function strategyVariant(strategy: string) {
  return STRATEGY_VARIANT[strategy] ?? "neutral"
}

function getEmailType(email: string): string {
  const domain = email.split("@")[1]?.toLowerCase() ?? ""
  if (domain.includes("gmail")) return "Gmail"
  if (domain.includes("outlook") || domain.includes("hotmail") || domain.includes("live"))
    return "Outlook"
  if (domain.includes("yahoo")) return "Yahoo"
  if (domain.includes("icloud") || domain.includes("me.com") || domain.includes("mac.com"))
    return "iCloud"
  if (domain.includes("proton")) return "Proton"
  return domain || "Unknown"
}

function formatAccountId(id: string): string {
  return id.replace(/^ACC-0*/, "")
}

/** Narrows a proxy option down to the fields the account form tracks. */
function toFormProxy(proxy: ProxyOption): AccountProxy {
  return {
    id: proxy.id,
    label: proxy.label,
    country: proxy.country,
    city: proxy.city,
  }
}

function formatDateTime(iso?: string): string {
  if (!iso) return ""
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ""
  return `${d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  })} ${d.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  })}`
}

function Accounts() {
  const { user } = useAuth()
  const isAdmin = user?.role === "ADMIN"
  const { data: accountsData, isLoading } = useAccounts()
  const data: Account[] = accountsData ?? []
  const { data: clientsData } = useClientOptions()
  const clients: ClientOption[] = clientsData ?? []
  const { data: proxiesData } = useProxies()
  const proxies: ProxyOption[] = proxiesData ?? []
  const createMut = useCreateAccount()
  const updateMut = useUpdateAccount()
  const revealMut = useRevealAccountPassword()
  const archiveMut = useArchiveAccount()
  const deleteMut = useDeleteAccount()

  const { logAction } = useLogs()
  const { t, locale } = useLocale()
  const [search, setSearch] = useState("")
  const [clientFilter, setClientFilter] = useState("")
  const [operatorFilter, setOperatorFilter] = useState("")
  const [lastActivitySort, setLastActivitySort] = useState<"none" | "asc" | "desc">("none")
  const [emailSort, setEmailSort] = useState<"none" | "asc" | "desc">("none")

  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [passwordTouched, setPasswordTouched] = useState(false)
  const [revealTarget, setRevealTarget] = useState<Account | null>(null)
  const [secretCode, setSecretCode] = useState("")
  const [revealedPasswords, setRevealedPasswords] = useState<Record<number, string>>({})

  const openAdd = () => {
    setEditingId(null)
    setForm(emptyForm)
    setPasswordTouched(false)
    setOpen(true)
  }

  const openEdit = (record: Account) => {
    setEditingId(record.id ?? null)
    setForm({ ...record, password: "" })
    setPasswordTouched(false)
    setOpen(true)
  }

  // The form keeps only the id so the assigned proxy always comes from the
  // freshest list, and the change log compares ids instead of objects.
  const selectedProxyId = form.proxy?.id ?? ""

  const handleProxyChange = (value: string) => {
    const proxy = proxies.find((p) => String(p.id) === value)
    setForm({ ...form, proxy: proxy ? toFormProxy(proxy) : null })
  }

  const describeProxy = (proxy: AccountProxy | null | undefined) => {
    if (!proxy) return "—"
    const location = formatProxyLocation(proxy, locale)
    return location ? `${proxy.label} · ${location}` : proxy.label
  }

  const clientAccountCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const row of data) counts[row.client] = (counts[row.client] ?? 0) + 1
    return counts
  }, [data])

  const operatorOptions = useMemo(
    () =>
      Array.from(new Set(data.map((r) => r.operator).filter((op): op is string => Boolean(op)))).sort(
        (a, b) => a.localeCompare(b),
      ),
    [data],
  )

  const filtered = useMemo(
    () =>
      data.filter((row) => {
        if (clientFilter && row.client !== clientFilter) return false
        if (operatorFilter && row.operator !== operatorFilter) return false
        // The proxy is a nested object, so the generic value scan skips it.
        const proxy = row.proxy
        if (
          search &&
          proxy &&
          [proxy.label, proxy.country, proxy.city].some((v) =>
            v.toLowerCase().includes(search.toLowerCase()),
          )
        ) {
          return true
        }
        return Object.values(row).some((v) => {
          if (Array.isArray(v)) return v.some((x) => x.toLowerCase().includes(search.toLowerCase()))
          if (typeof v === "string") return v.toLowerCase().includes(search.toLowerCase())
          return false
        })
      }),
    [data, search, clientFilter, operatorFilter],
  )

  const sorted = useMemo(() => {
    // Keep every account of the same client next to each other. The optional
    // column sorts only order the rows within each client group.
    const compareEmail = (a: Account, b: Account) => {
      const typeA = getEmailType(a.email)
      const typeB = getEmailType(b.email)
      const cmp = typeA.localeCompare(typeB)
      if (cmp !== 0) return emailSort === "asc" ? cmp : -cmp
      return emailSort === "asc" ? a.email.localeCompare(b.email) : b.email.localeCompare(a.email)
    }
    const compareLastActivity = (a: Account, b: Account) => {
      const aTime = a.lastActivity ? new Date(a.lastActivity).getTime() : 0
      const bTime = b.lastActivity ? new Date(b.lastActivity).getTime() : 0
      if (aTime === bTime) return 0
      if (aTime === 0) return 1
      if (bTime === 0) return -1
      return lastActivitySort === "asc" ? aTime - bTime : bTime - aTime
    }
    const compareWithinClient = (a: Account, b: Account) => {
      if (lastActivitySort !== "none") {
        const cmp = compareLastActivity(a, b)
        if (cmp !== 0) return cmp
      }
      if (emailSort !== "none") {
        const cmp = compareEmail(a, b)
        if (cmp !== 0) return cmp
      }
      return (a.accountId ?? "").localeCompare(b.accountId ?? "")
    }

    return [...filtered].sort((a, b) => {
      const byClient = a.client.localeCompare(b.client)
      if (byClient !== 0) return byClient
      return compareWithinClient(a, b)
    })
  }, [filtered, emailSort, lastActivitySort])

  // Archived accounts are shown in a separate table below the main one.
  const mainRows = useMemo(() => sorted.filter((row) => row.status !== "ARCHIVE"), [sorted])
  const archivedRows = useMemo(() => sorted.filter((row) => row.status === "ARCHIVE"), [sorted])

  const rememberRevealedPassword = (result: { id: number; password: string; hasPassword: boolean }) => {
    setRevealedPasswords((current) => {
      const next = { ...current }
      if (result.hasPassword) next[result.id] = result.password
      else delete next[result.id]
      return next
    })
  }

  const requestReveal = (record: Account) => {
    if (!record.id || !record.hasPassword) return
    if (isAdmin) {
      revealMut.mutate(
        { id: record.id },
        { onSuccess: rememberRevealedPassword },
      )
      return
    }
    setRevealTarget(record)
    setSecretCode("")
  }

  const submitReveal = (e: React.FormEvent) => {
    e.preventDefault()
    if (!revealTarget?.id) return
    revealMut.mutate(
      { id: revealTarget.id, secretCode },
      {
        onSuccess: (result) => {
          rememberRevealedPassword(result)
          setRevealTarget(null)
          setSecretCode("")
        },
      },
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const selectedClient = clients.find((c) => c.fullName === form.client)
    if (!selectedClient) return

    const dto: CreateAccountDto = {
      clientId: selectedClient.id,
      email: form.email,
      status: form.status,
      strategy: form.strategy,
      comment: form.comment,
      eldersOnly: form.eldersOnly ?? false,
      proxyId: form.proxy?.id ?? null,
    }
    if (editingId === null || passwordTouched) {
      dto.password = form.password ?? ""
    }

    if (editingId !== null) {
      const oldRecord = data.find((r) => r.id === editingId)
      if (oldRecord) {
        const changes: string[] = []
        for (const key of Object.keys(oldRecord) as (keyof Account)[]) {
          if (
            key === "id" ||
            key === "creationDate" ||
            key === "lastActivity" ||
            key === "password" ||
            key === "hasPassword" ||
            // Toggled straight from the table, not part of the edit form.
            key === "sms_verified" ||
            // Compared as an id below, since the objects differ by reference.
            key === "proxy"
          ) {
            continue
          }
          if (oldRecord[key] !== (dto as any)[key]) {
            changes.push(`${key}: ${oldRecord[key]} → ${(dto as any)[key]}`)
          }
        }
        const oldProxyId = oldRecord.proxy?.id ?? null
        const newProxyId = form.proxy?.id ?? null
        if (oldProxyId !== newProxyId) {
          changes.push(`proxy: ${describeProxy(oldRecord.proxy)} → ${describeProxy(form.proxy)}`)
        }
        if (passwordTouched) {
          changes.push(form.password ? "password: set" : "password: cleared")
        }
        if (changes.length > 0) {
          logAction(
            "accounts",
            "edit",
            `${form.accountId} (${form.client}): ${changes.join("; ")}`,
            form.client,
            form.accountId,
          )
        }
      }
      updateMut.mutate({ id: editingId, dto })
    } else {
      logAction(
        "accounts",
        "add",
        `Added account ${form.accountId} (${form.client})`,
        form.client,
        form.accountId,
      )
      createMut.mutate(dto)
    }
    setOpen(false)
    setForm(emptyForm)
    setEditingId(null)
    setPasswordTouched(false)
    // An updated password, or a changed account, invalidates previously
    // revealed plaintext values.
    setRevealedPasswords({})
  }

  const handleArchive = (record: Account) => {
    if (!record.id) return
    setRevealedPasswords({})
    logAction(
      "accounts",
      "edit",
      `Archived account ${record.accountId} (${record.client})`,
      record.client,
      record.accountId,
    )
    archiveMut.mutate(record.id)
  }

  const renderAccountRow = (row: Account) => (
    <TableRow key={row.id ?? row.accountId}>
      <TableCell className="font-mono text-xs font-medium">
        {formatAccountId(row.accountId)}
      </TableCell>
      <TableCell>
        <span className="flex items-center gap-2">
          <span
            className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
            style={{ backgroundColor: row.clientColor || getUserColor(row.client) }}
          />
          {row.client}
        </span>
      </TableCell>
      <TableCell className="font-mono text-sm">
        <span className="flex items-center gap-1.5">
          {row.email}
          <button
            onClick={() => {
              navigator.clipboard.writeText(row.email)
              toast(t("toast_copied"))
            }}
            className="h-6 w-6 rounded hover:bg-accent flex items-center justify-center transition-colors shrink-0"
            title={t("btn_copy")}
          >
            <Copy className="h-3 w-3" />
          </button>
        </span>
      </TableCell>
      <TableCell>
        {row.id != null && revealedPasswords[row.id] !== undefined ? (
          <span className="flex min-w-[150px] items-center gap-1.5">
            <span className="max-w-[180px] break-all font-mono text-xs">
              {revealedPasswords[row.id!]}
            </span>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(revealedPasswords[row.id!])
                toast(t("toast_copied"))
              }}
              className="h-6 w-6 rounded hover:bg-accent flex items-center justify-center transition-colors shrink-0"
              title={t("btn_copy_password")}
              aria-label={t("btn_copy_password")}
            >
              <Copy className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => {
                setRevealedPasswords((current) => {
                  const next = { ...current }
                  delete next[row.id as number]
                  return next
                })
              }}
              className="h-6 w-6 rounded hover:bg-accent flex items-center justify-center transition-colors shrink-0"
              title={t("btn_hide_password")}
              aria-label={t("btn_hide_password")}
            >
              <EyeOff className="h-3 w-3" />
            </button>
          </span>
        ) : row.hasPassword ? (
          <span className="flex items-center gap-1.5">
            <span className="font-mono text-xs tracking-widest text-muted-foreground">
              ••••••••
            </span>
            <button
              type="button"
              onClick={() => requestReveal(row)}
              disabled={revealMut.isPending && revealMut.variables?.id === row.id}
              className="h-6 w-6 rounded hover:bg-accent flex items-center justify-center transition-colors shrink-0 disabled:opacity-50"
              title={t("btn_reveal_password")}
              aria-label={t("btn_reveal_password")}
            >
              <Eye className="h-3 w-3" />
            </button>
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">
            {t("password_not_set")}
          </span>
        )}
      </TableCell>
      <TableCell>
        <Badge variant={statusVariant(row.status)}>
          {t(ACCOUNT_STATUS_T_KEY[row.status])}
        </Badge>
      </TableCell>
      <TableCell>
        <Badge variant={strategyVariant(row.strategy)}>
          {t(STRATEGY_T_KEY[row.strategy])}
        </Badge>
      </TableCell>
      <TableCell>
        {row.proxy ? (
          <span className="flex items-center gap-1.5 text-sm whitespace-nowrap">
            {countryFlag(row.proxy.country) && (
              <span aria-hidden="true">{countryFlag(row.proxy.country)}</span>
            )}
            <span className="text-xs text-muted-foreground">{row.proxy.label}</span>
            <span>{formatProxyLocation(row.proxy, locale)}</span>
          </span>
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        )}
      </TableCell>
      <TableCell className="text-sm">{row.creationDate}</TableCell>
      <TableCell className="text-sm whitespace-nowrap">
        {formatDateTime(row.lastActivity) || (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell>
        {row.comment ? (
          <span className="text-sm">{row.comment}</span>
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        )}
      </TableCell>
      <TableCell>
        {row.eldersOnly ? (
          <Badge variant="info" className="text-xs">
            Yes
          </Badge>
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        )}
      </TableCell>
      <TableCell>
        <span
          className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
          style={{
            backgroundColor:
              row.sms_verified === false ? "#e74c3c" : "#27ae60",
          }}
          role="img"
          aria-label={
            row.sms_verified === false
              ? t("sms_verify_needed")
              : t("sms_verified_ok")
          }
          title={
            row.sms_verified === false
              ? t("sms_verify_needed")
              : t("sms_verified_ok")
          }
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <button
            onClick={() => openEdit(row)}
            className="h-8 w-8 rounded-md border border-input bg-background flex items-center justify-center hover:bg-accent transition-colors"
            title={t("tip_edit")}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => handleArchive(row)}
            className="h-8 w-8 rounded-md border border-input bg-background flex items-center justify-center hover:bg-accent transition-colors"
            title={t("tip_archive")}
          >
            <Archive className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => {
              if (!row.id) return
              setRevealedPasswords({})
              deleteMut.mutate(row.id)
            }}
            className="h-8 w-8 rounded-md border border-input bg-background flex items-center justify-center hover:bg-accent transition-colors text-destructive"
            title={t("btn_delete")}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </TableCell>
    </TableRow>
  )

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("search_accounts")}
              className="h-10 w-full max-w-sm rounded-md border border-input bg-background pl-10 pr-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-w-[160px]"
          >
            <option value="">{t("filter_all_clients" as TranslationKey)}</option>
            {clients.map((c) => (
              <option key={c.id} value={c.fullName}>
                {c.fullName} ({clientAccountCounts[c.fullName] ?? 0})
              </option>
            ))}
          </select>
          {isAdmin && operatorOptions.length > 0 && (
            <select
              value={operatorFilter}
              onChange={(e) => setOperatorFilter(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">{t("filter_all_operators")}</option>
              {operatorOptions.map((op) => (
                <option key={op} value={op}>
                  {op}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Dialog
            open={open}
            onOpenChange={(v) => {
              if (!v) {
                setOpen(false)
                setEditingId(null)
              } else {
                setOpen(true)
              }
            }}
          >
            <DialogTrigger asChild>
              <button
                onClick={openAdd}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                <Plus className="h-4 w-4" />
                {t("btn_add_record")}
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingId !== null ? t("dialog_edit_account") : t("dialog_add_account")}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {editingId !== null && (
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium capitalize">
                        {t("form_account_id")}
                      </label>
                      <input
                        value={formatAccountId(form.accountId)}
                        readOnly
                        className="h-9 w-full rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground cursor-not-allowed"
                      />
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("th_client")}</label>
                    <select
                      value={form.client}
                      onChange={(e) => setForm({ ...form, client: e.target.value })}
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      required
                    >
                      <option value="" disabled>
                        {t("placeholder_select_account")}
                      </option>
                      {clients.map((c) => (
                        <option key={c.id} value={c.fullName}>
                          {c.fullName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium capitalize">
                      {t("form_email" as TranslationKey)}
                    </label>
                    <input
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="email"
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">
                      {t("form_account_password")}
                    </label>
                    <input
                      type="password"
                      value={form.password ?? ""}
                      onChange={(e) => {
                        setPasswordTouched(true)
                        setForm({ ...form, password: e.target.value })
                      }}
                      placeholder={t("form_account_password_optional")}
                      autoComplete="new-password"
                      maxLength={1024}
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                    <p className="text-xs text-muted-foreground">
                      {editingId !== null && form.hasPassword
                        ? t("form_account_password_keep")
                        : t("form_account_password_optional")}
                    </p>
                  </div>
                  <div className="space-y-1.5 col-span-2">
                    <label className="text-sm font-medium">{t("form_comment")}</label>
                    <textarea
                      value={form.comment}
                      onChange={(e) => setForm({ ...form, comment: e.target.value })}
                      placeholder={t("placeholder_add_comment")}
                      rows={3}
                      maxLength={1000}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                    />
                    <p className="text-xs text-muted-foreground text-right">
                      {form.comment.length}/1000
                    </p>
                  </div>
                  <div className="flex items-center justify-between col-span-2">
                    <label className="text-sm font-medium">Elders Only</label>
                    <Switch
                      checked={form.eldersOnly ?? false}
                      onCheckedChange={(checked) => setForm({ ...form, eldersOnly: checked })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("form_proxy")}</label>
                    <select
                      value={selectedProxyId}
                      onChange={(e) => handleProxyChange(e.target.value)}
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <option value="">{t("placeholder_select_proxy")}</option>
                      {proxies.map((p) => {
                        const location = formatProxyLocation(p, locale)
                        return (
                          <option key={p.id} value={p.id}>
                            {location
                              ? `${p.label} · ${countryFlag(p.country)} ${location}`
                              : p.label}
                          </option>
                        )
                      })}
                    </select>
                    {proxies.length === 0 && (
                      <p className="text-xs text-muted-foreground">{t("form_proxy_hint")}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("form_strategy")}</label>
                    <select
                      value={form.strategy}
                      onChange={(e) => setForm({ ...form, strategy: e.target.value })}
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      required
                    >
                      <option value="" disabled>
                        {t("placeholder_select_strategy")}
                      </option>
                      {/* Outsource is no longer offered for new selections,
                          but existing records keep showing their current value. */}
                      {form.strategy === "Outsource" && (
                        <option value="Outsource">{t("strategy_outsource")}</option>
                      )}
                      {STRATEGY_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {t(STRATEGY_T_KEY[s])}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium capitalize">{t("form_status")}</label>
                    <select
                      value={form.status}
                      onChange={(e) =>
                        setForm({ ...form, status: e.target.value as AccountStatus })
                      }
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      required
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {t(ACCOUNT_STATUS_T_KEY[s])}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <DialogClose asChild>
                    <button
                      type="button"
                      className="h-9 px-4 rounded-md border border-input bg-background text-sm hover:bg-accent transition-colors"
                    >
                      {t("btn_cancel")}
                    </button>
                  </DialogClose>
                  <button
                    type="submit"
                    className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                  >
                    {t("btn_save")}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("th_account_id")}</TableHead>
              <TableHead>{t("th_client")}</TableHead>
              <TableHead>
                <button
                  type="button"
                  onClick={() =>
                    setEmailSort(
                      emailSort === "none" ? "asc" : emailSort === "asc" ? "desc" : "none",
                    )
                  }
                  className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                >
                  {t("th_email")}
                  {emailSort === "asc" ? (
                    <ArrowUp className="h-3.5 w-3.5" />
                  ) : emailSort === "desc" ? (
                    <ArrowDown className="h-3.5 w-3.5" />
                  ) : (
                    <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
                  )}
                </button>
              </TableHead>
              <TableHead>{t("th_password")}</TableHead>
              <TableHead>{t("th_status")}</TableHead>
              <TableHead>{t("th_strategy")}</TableHead>
              <TableHead>{t("th_proxy")}</TableHead>
              <TableHead>{t("th_creation_date")}</TableHead>
              <TableHead>
                <button
                  type="button"
                  onClick={() =>
                    setLastActivitySort(
                      lastActivitySort === "none"
                        ? "asc"
                        : lastActivitySort === "asc"
                          ? "desc"
                          : "none",
                    )
                  }
                  className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                >
                  {t("th_last_activity")}
                  {lastActivitySort === "asc" ? (
                    <ArrowUp className="h-3.5 w-3.5" />
                  ) : lastActivitySort === "desc" ? (
                    <ArrowDown className="h-3.5 w-3.5" />
                  ) : (
                    <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
                  )}
                </button>
              </TableHead>
              <TableHead>{t("th_comment")}</TableHead>
              <TableHead>Elders</TableHead>
              <TableHead>{t("th_sms_verified")}</TableHead>
              <TableHead className="w-16">{t("th_actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {mainRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} className="text-center text-muted-foreground py-8">
                  {t("empty_no_results")}
                </TableCell>
              </TableRow>
            ) : (
              mainRows.map(renderAccountRow)
            )}
          </TableBody>
        </Table>
      </div>

      {archivedRows.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold">{t("accounts_archived")}</h2>
            <span className="text-xs text-muted-foreground">
              {t("accounts_archived_count", String(archivedRows.length))}
            </span>
          </div>
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("th_account_id")}</TableHead>
                  <TableHead>{t("th_client")}</TableHead>
                  <TableHead>{t("th_email")}</TableHead>
                  <TableHead>{t("th_password")}</TableHead>
                  <TableHead>{t("th_status")}</TableHead>
                  <TableHead>{t("th_strategy")}</TableHead>
                  <TableHead>{t("th_proxy")}</TableHead>
                  <TableHead>{t("th_creation_date")}</TableHead>
                  <TableHead>{t("th_last_activity")}</TableHead>
                  <TableHead>{t("th_comment")}</TableHead>
                  <TableHead>Elders</TableHead>
                  <TableHead>{t("th_sms_verified")}</TableHead>
                  <TableHead className="w-16">{t("th_actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>{archivedRows.map(renderAccountRow)}</TableBody>
            </Table>
          </div>
        </div>
      )}

      <Dialog
        open={revealTarget !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setRevealTarget(null)
            setSecretCode("")
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("dialog_reveal_password")}</DialogTitle>
            <p className="text-sm text-muted-foreground">
              {revealTarget?.accountId} · {revealTarget?.client}
            </p>
          </DialogHeader>
          <form onSubmit={submitReveal} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="account-reveal-code">
                {t("form_secret_code")}
              </label>
              <input
                id="account-reveal-code"
                type="password"
                value={secretCode}
                onChange={(e) => setSecretCode(e.target.value)}
                autoComplete="off"
                required
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <p className="text-xs text-muted-foreground">
                {t("form_secret_code_hint")}
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setRevealTarget(null)
                  setSecretCode("")
                }}
                className="h-9 rounded-md border border-input bg-background px-4 text-sm font-medium hover:bg-accent transition-colors"
              >
                {t("btn_cancel")}
              </button>
              <button
                type="submit"
                disabled={revealMut.isPending || !secretCode.trim()}
                className="h-9 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {t("btn_reveal_password")}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default Accounts
